import { ExpandCircleDown } from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  Typography
} from '@mui/material'
import { ProfileImage } from '../../../customComponents/user/ProfileImage'
import { type ISDSummary } from './BudgetAccordion'
import { BudgetActivityDisplay } from './BudgetActivityDisplay'
import { useState } from 'react'
import { Comments } from '../../../customComponents/comments/Comments'
import { CommentParentType } from '../../../../models/Comment'
import { ORG_AVATAR_COLORS, ORG_CARD_COLORS } from '../../../../utils/colors'
import { type AnnualPlanRole } from '../../../../models/AnnualPlanRole'
import { PriorityStatusButton } from '../priorityActivities/PriorityStatusButton'
import { useUpdatePlanOrgTaskStatus } from '../../../../hooks/use-updatePlanOrgTaskStatus'
import { type PlanOrganizationTask } from '../../../../models/PlanOrganizationTask'
import { OverviewStatusModals } from '../priorityActivities/priorityFour/OverviewStatusModals'
import { SurpassesThreshold } from './SurpassesThreshold'

interface Props {
  summary: ISDSummary
  userRole: AnnualPlanRole | null
  hideButtons?: boolean
  implementationPhase?: boolean
}

export const BudgetISDAccordion = (props: Props): JSX.Element => {
  const handlePlanOrgTaskStatusUpdated = (updatedTask: PlanOrganizationTask): void => {
    props.summary.planOrganizationTask = updatedTask
  }
  const {
    isUpdating,
    handleConfirmLockModal,
    updatePlanOrgTaskStatus,
    statusForLockModal,
    setStatusForLockModal,
    statusForSomeItemsLockModal,
    setStatusForSomeItemsLockModal,
    statusForSubmittedToInProgressModal,
    setStatusForSubmittedToInProgressModal,
    statusForSubmittedToAcceptedModal,
    setStatusForSubmittedToAcceptedModal,
    statusForSubmittedToNeedsRevisionModal,
    setStatusForSubmittedToNeedsRevisionModal,
    handleConfirmSubmittedToNeedsRevision,
    disableStatus
  } = useUpdatePlanOrgTaskStatus({
    userRole: props.userRole,
    annualPlanId: props.summary.annualPlanId,
    planOrganizationTask: props.summary.planOrganizationTask,
    onPlanOrgTaskUpdate: handlePlanOrgTaskStatusUpdated,
    contractPhase: true
  })
  const [expanded, setExpanded] = useState(false)

  const backgroundColor = ORG_CARD_COLORS[props.summary.isdName.charCodeAt(0) % ORG_CARD_COLORS.length]
  const avatarColor = ORG_AVATAR_COLORS[props.summary.isdName.charCodeAt(0) % ORG_AVATAR_COLORS.length]

  return <Accordion
    expanded={expanded}
    disableGutters={true}
    elevation={0}
    onChange={() => { setExpanded(!expanded) }}
    data-testid={`accordion-${props.summary.isdName}`}
    sx={{ '&:before': { display: 'none' } }}
  >
    <AccordionSummary
      expandIcon={<ExpandCircleDown sx={{ color: 'var(--blue-700)', visibility: props.hideButtons === true ? 'hidden' : 'visible' }} data-testid={`expand-${props.summary.isdName}`} />}
      role='none presentation'
      aria-expanded={undefined}
      sx={{
        bgcolor: backgroundColor,
        borderTopLeftRadius: '12px',
        borderTopRightRadius: '12px',
        borderBottomLeftRadius: expanded ? '0' : '12px',
        borderBottomRightRadius: expanded ? '0' : '12px',
        px: '20px',
        py: '12px'
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <ProfileImage
            width='3em'
            height='3em'
            name={props.summary.isdName}
            backgroundColor={avatarColor}
            imageKey={props.summary.isdImageKey}
            sx={{ mr: '1em' }}
          />

          <Box>
            {props.implementationPhase === true && props.summary.needsReview && !expanded && <SurpassesThreshold/>}
            <Typography variant={expanded ? 'h3' : 'h4'}>
              {props.summary.isdName}
            </Typography>

            {expanded &&
              <Typography variant='body1' sx={{ fontSize: '16px', color: 'var(--grey-700)' }}>
                Budget requests broken down by Core Activities
              </Typography>
            }
          </Box>
        </Box>

        {(!expanded || props.hideButtons === true) &&
          (props.implementationPhase === true
            ? (
              <Box sx={{ textAlign: 'right', mr: '.5em' }}>
                <Typography variant='h3' data-testid={`totalISDAmount-${props.summary.isdName}`}>
                  Contract: {props.summary.totalAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                </Typography>
                <Typography variant='h3' data-testid={`totalISDUpdatedAmount-${props.summary.isdName}`}>
                  Updates: {props.summary.totalUpdatedAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                </Typography>
              </Box>
              )
            : (
              <Typography variant='h3' sx={{ mr: '.5em' }} data-testid={`totalISDAmount-${props.summary.isdName}`}>
                {props.summary.totalAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
              </Typography>
              )
          )
        }

        {expanded && props.summary.planOrganizationTask != null && props.hideButtons !== true &&
          <Box sx={{ display: 'flex', mr: '1em', gap: '1em' }}>
            <>
              <PriorityStatusButton
                currentStatus={props.summary.planOrganizationTask.status}
                onChange={updatePlanOrgTaskStatus}
                userRole={props.userRole}
                disabled={isUpdating || disableStatus}
              />
              <OverviewStatusModals
                statusForLockModal={statusForLockModal}
                handleConfirmLockModal={handleConfirmLockModal}
                handleCloseLockModal={() => { setStatusForLockModal(null) }}
                statusForSomeItemsLockModal={statusForSomeItemsLockModal}
                handleConfirmNoItemsLockModal={handleConfirmLockModal}
                handleCloseNoItemsLockModal={() => { setStatusForSomeItemsLockModal(null) }}
                statusForSubmittedToInProgressModal={statusForSubmittedToInProgressModal}
                handleConfirmSubmittedToInProgressModal={handleConfirmLockModal}
                handleCloseSubmittedToInProgressModal={() => { setStatusForSubmittedToInProgressModal(null) }}
                statusForSubmittedToAcceptedModal={statusForSubmittedToAcceptedModal}
                handleConfirmSubmittedToAcceptedModal={handleConfirmLockModal}
                handleCloseSubmittedToAcceptedModal={() => { setStatusForSubmittedToAcceptedModal(null) }}
                statusForSubmittedToNeedsRevisionModal={statusForSubmittedToNeedsRevisionModal}
                handleConfirmSubmittedToNeedsRevision={handleConfirmSubmittedToNeedsRevision}
                handleCloseSubmittedToNeedsRevision={() => {
                  setStatusForSubmittedToNeedsRevisionModal(null)
                }}
                activities={props.summary.priorityActivities ?? []}
                planOrganizationTaskId={props.summary.planOrganizationTask.id}
                priorityNumber={props.summary.priorityNumber ?? 0}
              />
            </>

            <Comments
              parentType={CommentParentType.PlanOrganizationTask}
              parentId={props.summary.planOrganizationTask.id}
            />
          </Box>
        }
      </Box>
    </AccordionSummary>

    <AccordionDetails sx={{ bgcolor: backgroundColor, borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px', breakInside: 'avoid' }}>
      <Stack spacing='1em' sx={{ bgcolor: 'white', borderRadius: '12px', p: '20px' }}>
        {props.summary.budgetItems.map((item, index) => (
          <BudgetActivityDisplay
            key={index}
            name={item.name}
            amount={item.amount}
            updatedAmount={item.updatedAmount}
            needsReview={item.needsReview}
            viewDetails={item.viewDetails}
            color={item.color}
            planOrganizationId={props.summary?.planOrganizationId ?? 0}
            priorityId={props.summary?.priorityId ?? 0}
            annualPlanId={props.summary.annualPlanId}
            rcnId={props.summary.rcnId}
            userRole={props.userRole}
            hideButtons={props.hideButtons}
            implementationPhase={props.implementationPhase}
          />
        ))}

        <Typography variant='h3' sx={{ textAlign: 'right' }} data-testid='totalISDAmountDetails'>
          Total: {props.summary.totalAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
        </Typography>
      </Stack>
    </AccordionDetails>
  </Accordion>
}
